/**
 * First home buyer transfer (stamp) duty concession caps, by state.
 *
 * EVERY FIGURE IN THIS FILE IS A SNAPSHOT. Caps are set by each state and
 * territory and change with budgets and legislation. This is the single place
 * to edit them — nothing here is duplicated in the UI. When you update a state,
 * bump FIGURES_AS_AT so the panel stops claiming figures are newer than they
 * are.
 */
import type { AustralianState } from '../types/roadmap'

export const FIGURES_AS_AT = 'August 2026'

/**
 * How a state treats an eligible first home buyer.
 *
 * - 'threshold'  nil duty up to `nilUpTo`, then a partial concession that runs
 *                out at `concessionUpTo`, above which the standard scale
 *                applies in full.
 * - 'exempt'     no duty regardless of price, subject to the state's own
 *                eligibility tests.
 * - 'none'       no duty concession offered.
 */
export type ConcessionKind = 'threshold' | 'exempt' | 'none'

export interface StateConcession {
  code: AustralianState
  name: string
  kind: ConcessionKind
  /** Buy at or under this and an eligible first home buyer pays no duty. */
  nilUpTo?: number
  /** The concession tapers to nothing by this price. */
  concessionUpTo?: number
}

export const STATE_DUTY: Record<AustralianState, StateConcession> = {
  WA: {
    code: 'WA',
    name: 'Western Australia',
    kind: 'threshold',
    nilUpTo: 500000,
    concessionUpTo: 700000,
  },
  NSW: {
    code: 'NSW',
    name: 'New South Wales',
    kind: 'threshold',
    nilUpTo: 800000,
    concessionUpTo: 1000000,
  },
  VIC: {
    code: 'VIC',
    name: 'Victoria',
    kind: 'threshold',
    nilUpTo: 600000,
    concessionUpTo: 750000,
  },
  QLD: {
    code: 'QLD',
    name: 'Queensland',
    kind: 'threshold',
    nilUpTo: 700000,
    concessionUpTo: 800000,
  },
  SA: {
    // New homes only — no price cap, but an established home gets nothing.
    code: 'SA',
    name: 'South Australia',
    kind: 'exempt',
  },
  TAS: {
    // No partial phase-out: over the cap, the full rate applies.
    code: 'TAS',
    name: 'Tasmania',
    kind: 'threshold',
    nilUpTo: 750000,
  },
  ACT: {
    // Income-tested rather than price-tested.
    code: 'ACT',
    name: 'Australian Capital Territory',
    kind: 'exempt',
  },
  NT: {
    // Support comes as cash grants, which do not reduce the duty itself.
    code: 'NT',
    name: 'Northern Territory',
    kind: 'none',
  },
}

export const STATE_ORDER: AustralianState[] = [
  'WA',
  'NSW',
  'VIC',
  'QLD',
  'SA',
  'TAS',
  'ACT',
  'NT',
]

/**
 * Purchasing costs expressed as a share of the property price.
 *
 * A range, not a single figure, because stamp duty is the largest component and
 * it swings enormously — an eligible first home buyer under their state's cap
 * pays none at all, while the same person just above it pays the full scale.
 * The low end assumes a concession applies; the high end assumes it does not.
 */
export const COST_RANGE = {
  firstHome: {
    low: 0.01,
    high: 0.05,
  },
  investment: {
    low: 0.04,
    high: 0.06,
  },
}

export const DUTY_DISCLAIMER =
  'Estimates only. What you actually pay depends on the property, your eligibility and the rules at the time you buy. Confirm with your state revenue office, or ask Liam.'

export const LOWER_DEPOSIT_NOTE =
  'There are specific options that let both first home buyers and investors get in with a smaller deposit than this. Whether one fits you depends on your situation — ask and find out.'

export const LMI_NOTE =
  'Worth knowing: a smaller deposit usually costs more overall, not less. Under 20% you generally pay lenders mortgage insurance and your repayments are higher.'

export const PILLARS_NOTE =
  'Three checkpoints instead of one distant number. The first is deliberately the easiest — getting there early is what keeps the habit going.'

export const CALENDAR_NOTE =
  'Four events built on your device: your three check-ins and your 90-day review. Nothing is sent anywhere. The dates are a snapshot of today’s plan, so redo your roadmap if your savings change.'
